import React from "react";
import { DatePicker, Button, ConfigProvider, Select, message } from "antd";
import ruRU from "antd/es/locale/ru_RU";
import enUS from "antd/es/locale/en_US";
import ptPT from "antd/es/locale/pt_PT";
import esES from "antd/es/locale/es_ES";
import BackButton from "@/note_office/shared/BackButton";
import { useNavigate } from "react-router-dom";
import { useClientContactsState } from "../State";
import translator from "@/translator";
import useBulkDelivery from "../hooks/useBulkDelivery";
import useScheduleTime from "../hooks/useScheduleTime";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import localeData from "dayjs/plugin/localeData";
import "dayjs/locale/ru";
import "dayjs/locale/en";
import "dayjs/locale/pt";
import "dayjs/locale/es";

dayjs.extend(weekday);
dayjs.extend(localeData);

const antdLocales = {
  ru: ruRU,
  en: enUS,
  pt: ptPT,
  es: esES,
};

const ScheduleView = () => {
  const navigate = useNavigate();
  const { state } = useClientContactsState();
  const {
    bulkDeliveriesData = {},
    selectedTemplateText = "",
    selectedChannels = {},
    selectedContacts = [],
  } = state;
  const { create_url, customer, timezones = [], customer_timezone } = bulkDeliveriesData;

  const { isLoading, sendBulkDelivery } = useBulkDelivery();

  const customerLocale = (customer?.locale || "en").slice(0, 2);
  const {
    date,
    setDate,
    hour,
    setHour,
    minute,
    setMinute,
    timezone,
    setTimezone,
    disabledDate,
    hourOptions,
    minuteOptions,
    scheduledAt,
  } = useScheduleTime({ customerTimezone: customer_timezone, timezones });

  const t = translator("note_office.views.bulk_deliveries.schedule_view.");

  const handleSchedule = async () => {
    try {
      await sendBulkDelivery({
        create_url,
        text: selectedTemplateText,
        channels: selectedChannels,
        chategy_customer_client_ids: selectedContacts,
        scheduled_at: scheduledAt,
        onSuccess: () => navigate("/schedule_delivery"),
        onError: (errors) => {
          Object.entries(errors).forEach(([_key, value]) => {
            message.error(value);
          });
        },
      });
    } catch {
      message.error(t("errors.server_error"));
    }
  };

  return (
    <ConfigProvider locale={antdLocales[customerLocale] || enUS}>
      <div className="wrapper wrapper__menu-layout overflow">
        <BackButton onClick={() => navigate("/create")} text={t("title")} />

        <div className="schedule__container">
          <div className="schedule__content">
            <div className="schedule__time">
              <Select value={hour} onChange={setHour} className="schedule__time-hour">
                {hourOptions.map((h) => (
                  <Select.Option key={h} value={h}>
                    {String(h).padStart(2, "0")}
                  </Select.Option>
                ))}
              </Select>
              <Select value={minute} onChange={setMinute} className="schedule__time-minute">
                {minuteOptions.map((m) => (
                  <Select.Option key={m} value={m}>
                    {String(m).padStart(2, "0")}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div className="schedule__date">
              <DatePicker
                popupClassName="calendar"
                value={date}
                onChange={(value) => value && !disabledDate(value) && setDate(value)}
                format="DD.MM.YYYY"
                disabledDate={disabledDate}
              />
            </div>

            <Select value={timezone} onChange={setTimezone} className="schedule__time-timezone">
              {timezones.map(([label, value]) => (
                <Select.Option key={value} value={value}>
                  {label}
                </Select.Option>
              ))}
            </Select>
          </div>

          <Button
            type="primary"
            block
            className="button-base"
            onClick={handleSchedule}
            loading={isLoading}
            disabled={isLoading || selectedContacts.length === 0}
          >
            {t("schedule")}
          </Button>
        </div>
      </div>
    </ConfigProvider>
  );
};

export default ScheduleView;
